import { useEffect, useRef, useState, KeyboardEvent, FC } from 'react';
import BaseFormControl from '@mui/material/FormControl';
import { fetchStoreListAPI } from '@/api/stores';
import {
    Autocomplete,
    LinearProgress,
    CircularProgress,
    InputAdornment,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import { useAppContext } from '@/provider/contexts';
import { useTranslation } from 'react-i18next';
import localforage from 'localforage';

interface Store {
    store_id: number;
    store_name: string;
}

const STORE_LIST_STALE_TIME_MS = 30_000;

interface StoreAutocompleteProps {
    storeId?: number | null;
    preloadedStoreOptions?: Store[];
    onStoreChange: (storeId: number) => void;
    onKeyUp?: (e: KeyboardEvent<Element>) => void;
}

const StoreAutocomplete: FC<StoreAutocompleteProps> = ({ storeId, onStoreChange, preloadedStoreOptions, onKeyUp }) => {
    const { t } = useTranslation('common');
    const { setManageStoreName } = useAppContext();
    const [storeOptions, setStoreOptions] = useState<Store[]>([]);
    const [loading, setLoading] = useState(false);
    const lastFetchedAtRef = useRef(0);

    const fetchStoreListHandler = async () => {
        setLoading(true);
        try {
            const cid = (await localforage.getItem('corporation_id')) as number;
            const params = { cid: cid };
            const {
                data: { data },
            } = await fetchStoreListAPI(params);
            const stores = data.aaData || [];
            stores.sort(sortStoresByName);
            localStorage.setItem('hasMultiStore', String(stores.length > 0));
            setStoreOptions(stores);
            lastFetchedAtRef.current = Date.now();
        } catch (error) {
            console.error('Error fetching store list:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDropdownOpen = () => {
        if (preloadedStoreOptions) return;
        if (Date.now() - lastFetchedAtRef.current > STORE_LIST_STALE_TIME_MS) {
            fetchStoreListHandler();
        }
    };

    const sortStoresByName = (a: Store, b: Store) => {
        if (a.store_name < b.store_name) return -1;
        if (a.store_name > b.store_name) return 1;
        return 0;
    };

    useEffect(() => {
        if (preloadedStoreOptions) {
            setStoreOptions(preloadedStoreOptions);
        } else {
            fetchStoreListHandler();
        }
    }, []);

    return (
        <BaseFormControl fullWidth variant="outlined">
            <Autocomplete
                id="store-select"
                getOptionKey={(option) => option.store_id}
                value={storeId && storeOptions.length ? storeOptions.find((store) => store.store_id === storeId) : null}
                options={storeOptions}
                getOptionLabel={(option) => option.store_name}
                isOptionEqualToValue={(option, value) => option.store_id === value.store_id}
                loading={loading}
                loadingText={
                    <>
                        <LinearProgress />
                        <Typography sx={{ ml: 0.5, mt: 1.5 }}> {t('Loading Stores...')}</Typography>
                    </>
                }
                noOptionsText={t('No store data available.')}
                onChange={(_, value) => {
                    // Ignore selections made while a background refetch (see
                    // handleDropdownOpen) is in flight — storeOptions may be
                    // about to be replaced by a fresher list.
                    if (loading) return;
                    const selectedStore = storeOptions.find((store) => store.store_id === value?.store_id);
                    if (selectedStore) {
                        onStoreChange(selectedStore.store_id);
                        setManageStoreName(selectedStore.store_name);
                    }
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={t('Store')}
                        slotProps={{
                            input: {
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loading ? (
                                            <InputAdornment position="end">
                                                {<CircularProgress color="primary" size={26} />}
                                            </InputAdornment>
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            },
                        }}
                    />
                )}
                slots={{
                    paper: (props) => <Paper sx={{ maxHeight: '30vh', overflowY: 'auto' }} {...props} />,
                }}
                onOpen={handleDropdownOpen}
                onKeyUp={onKeyUp}
            />
        </BaseFormControl>
    );
};

export default StoreAutocomplete;
